import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, cant, buscar } from '../utils/formato';

// Crear y corregir productos.
//
// La parte que hay que explicar bien es la del empaque: los tornillos se
// COMPRAN por caja de mil y se VENDEN por unidad. Si eso queda mal puesto, cada
// entrada de mercancía va a meter mil veces más o mil veces menos de lo que
// debe, y el inventario deja de servir.

const UNIDADES = ['unidad', 'lámina', 'metro', 'kilo', 'libra', 'litro',
                  'docena', 'rollo', 'bulto', 'galón'];

const vacio = {
  codigo: '', nombre: '', referencia: '', categoria_id: '', descripcion: '',
  unidad: 'unidad', unidades_por_empaque: 1, nombre_empaque: 'unidad',
  precio_venta: '', stock_minimo: '',
};

function Productos({ onCambio }) {
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState(null);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      axios.get('/api/inventario').then(r => setProductos(r.data || [])),
      axios.get('/api/categorias').then(r => setCategorias(r.data || [])),
    ]).catch(() => {}).finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const visibles = buscar(productos, busqueda);
  const c = (k) => ({
    value: form?.[k] ?? '',
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  const abrirNuevo = () => { setEditando(null); setForm({ ...vacio }); setAviso(null); };
  const abrirEdicion = (p) => {
    setEditando(p);
    setForm({
      codigo: p.codigo, nombre: p.nombre, referencia: p.referencia || '',
      categoria_id: p.categoria_id || '', descripcion: p.descripcion || '',
      unidad: p.unidad, unidades_por_empaque: p.unidades_por_empaque,
      nombre_empaque: p.nombre_empaque || 'unidad',
      precio_venta: p.precio_venta, stock_minimo: p.stock_minimo,
    });
    setAviso(null);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    const cuerpo = {
      ...form,
      categoria_id: form.categoria_id || null,
      unidades_por_empaque: parseFloat(form.unidades_por_empaque) || 1,
      precio_venta: parseFloat(form.precio_venta) || 0,
      stock_minimo: parseFloat(form.stock_minimo) || 0,
    };
    try {
      if (editando) await axios.put(`/api/productos/${editando.id}`, cuerpo);
      else await axios.post('/api/productos', cuerpo);
      setAviso({ tipo: 'bueno',
        texto: `${form.nombre} ${editando ? 'corregido' : 'creado'}` });
      setForm(null); setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const desactivar = async (p) => {
    if (!window.confirm(
      `¿Quitar ${p.nombre} del catálogo?\n\n` +
      'No se borra: deja de aparecer al vender, pero las ventas que ya lo llevaban ' +
      'siguen intactas.'
    )) return;
    try {
      await axios.put(`/api/productos/${p.id}`, { activo: 0 });
      cargar();
      onCambio && onCambio();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo quitar');
    }
  };

  // Cuánto deja este producto con el precio que se está escribiendo
  const porEmpaque = parseFloat(form?.unidades_por_empaque) || 1;
  const precio = parseFloat(form?.precio_venta) || 0;
  const costo = editando?.costo_unitario || 0;

  return (
    <>
      <div className="encabezado">
        <h1>Productos</h1>
        <p>El catálogo: códigos, unidades y precios</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {form && (
        <div className="bloque">
          <h2>{editando ? `✏️ ${editando.nombre}` : '➕ Nuevo producto'}</h2>
          <form className="cuerpo" onSubmit={guardar}>
            <div className="campos">
              <label className="campo">
                <span>Código *</span>
                <input {...c('codigo')} required placeholder="TOR-38" />
                <small style={{ color: 'var(--tinta-tenue)' }}>
                  con el que tú lo llamas
                </small>
              </label>
              <label className="campo">
                <span>Nombre *</span>
                <input {...c('nombre')} required
                       placeholder='Tornillo autoperforante 3/8"' />
              </label>
              <label className="campo">
                <span>Referencia</span>
                <input {...c('referencia')} placeholder="La del fabricante" />
                <small style={{ color: 'var(--tinta-tenue)' }}>
                  a veces es lo único que trae la factura
                </small>
              </label>
              <label className="campo">
                <span>Categoría</span>
                <select {...c('categoria_id')}>
                  <option value="">— Sin categoría —</option>
                  {categorias.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                </select>
              </label>
            </div>

            {/* La parte que hay que acertar */}
            <div style={{ marginTop: 18, padding: 15, borderRadius: 'var(--radio)',
                          background: 'var(--acero-piso)', border: '1px solid var(--acero)' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Cómo se compra y cómo se vende
              </div>
              <p style={{ margin: '0 0 13px', fontSize: 13, color: 'var(--tinta-media)' }}>
                Los tornillos se compran por caja y se venden por unidad. Si esto queda
                mal puesto, cada entrada va a meter mil veces más o mil veces menos de
                lo que debe.
              </p>
              <div className="campos">
                <label className="campo">
                  <span>Se VENDE por</span>
                  <select {...c('unidad')}>
                    {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Se COMPRA por</span>
                  <input {...c('nombre_empaque')} placeholder="caja, bulto, rollo…" />
                </label>
                <label className="campo">
                  <span>Cuántas trae ese empaque</span>
                  <input type="number" step="0.01" min="1" className="num-grande"
                         {...c('unidades_por_empaque')} />
                </label>
              </div>
              <div className="aviso" style={{ marginTop: 13, marginBottom: 0 }}>
                {porEmpaque > 1
                  ? <>Un <strong>{form.nombre_empaque || 'empaque'}</strong> trae{' '}
                      <strong>{cant(porEmpaque)} {form.unidad}</strong>. Al anotar la
                      compra escribes cuántos {form.nombre_empaque || 'empaques'} llegaron
                      y el sistema hace la cuenta.</>
                  : <>Se compra y se vende igual, por <strong>{form.unidad}</strong>.
                      No hay conversión que hacer.</>}
              </div>
            </div>

            <div className="campos" style={{ marginTop: 18 }}>
              <label className="campo">
                <span>Precio de venta (por {form.unidad}) *</span>
                <input type="number" step="1" min="0" className="num-grande"
                       required {...c('precio_venta')} />
              </label>
              <label className="campo">
                <span>Avisar cuando queden menos de</span>
                <input type="number" step="0.01" min="0" className="num-grande"
                       {...c('stock_minimo')} placeholder="0 = no avisar" />
                <small style={{ color: 'var(--tinta-tenue)' }}>
                  en {form.unidad}
                </small>
              </label>
            </div>

            {editando && costo > 0 && precio > 0 && (
              <div className={'aviso ' + (precio <= costo ? 'malo' : '')}
                   style={{ marginTop: 16, marginBottom: 0 }}>
                Costó <strong>{plata(costo)}</strong> y se vende a{' '}
                <strong>{plata(precio)}</strong>:{' '}
                {precio > costo
                  ? <>deja <strong>{plata(precio - costo)}</strong> por {form.unidad}{' '}
                     ({((precio - costo) / costo * 100).toFixed(0)}%)</>
                  : <>⚠️ <strong>se pierde {plata(costo - precio)}</strong> en cada {form.unidad}</>}
              </div>
            )}

            <div className="acciones">
              <button type="button" className="boton suave"
                      onClick={() => { setForm(null); setEditando(null); }}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bloque">
        <h2>
          🏷️ Catálogo
          <span className="contador">
            {visibles.length}{visibles.length !== productos.length && ` de ${productos.length}`}
          </span>
          {!form && (
            <button className="boton chico" style={{ marginLeft: 'auto' }} onClick={abrirNuevo}>
              ➕ Nuevo producto
            </button>
          )}
        </h2>

        <div className="cuerpo" style={{ paddingBottom: 0 }}>
          <div className="buscador">
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                   placeholder="🔎 Buscar por código, nombre o referencia" />
            {busqueda && <button type="button" onClick={() => setBusqueda('')}>×</button>}
          </div>
        </div>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : visibles.length === 0 ? (
          <div className="vacio">
            {busqueda ? `Ningún producto coincide con «${busqueda}»`
                      : 'Todavía no hay productos. Crea el primero.'}
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Producto</th>
                  <th>Se vende por</th>
                  <th>Se compra por</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                  <th style={{ textAlign: 'right' }}>Mínimo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(p => (
                  <tr key={p.id}>
                    <td className="codigo">{p.codigo}</td>
                    <td className="recio">
                      {p.nombre}
                      {p.referencia && (
                        <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                          ref. {p.referencia}
                        </div>
                      )}
                    </td>
                    <td>{p.unidad}</td>
                    <td>
                      {p.unidades_por_empaque > 1
                        ? <>{p.nombre_empaque} de {cant(p.unidades_por_empaque)}</>
                        : <span style={{ color: 'var(--tinta-tenue)' }}>igual</span>}
                    </td>
                    <td className="num recio">{plata(p.precio_venta)}</td>
                    <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                      {p.stock_minimo > 0 ? cant(p.stock_minimo) : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="boton chico suave" style={{ marginRight: 5 }}
                              onClick={() => abrirEdicion(p)}>✏️</button>
                      <button className="boton chico suave" onClick={() => desactivar(p)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Productos;
